import axios from "axios";
import { compareAsc, startOfWeek } from "date-fns";
import { format, utcToZonedTime } from "date-fns-tz";

export const axiosAuth = axios.create({
	//baseURL: `https://qa-api.bubo.io/auth/`,
	baseURL: `https://${process.env.REACT_APP_AUTH_DOMAIN}/auth/`,
});

const protocol = process.env.NODE_ENV === "development" ? "http" : "https";

export const axiosShyftr = axios.create({
	baseURL: `${protocol}://${process.env.REACT_APP_API_DOMAIN}/shyftr/`,
});

const parseMe = (data) => {
	return {
		locationAssignmentId: data.location_assignment_id,
		name: data.bubo_contract_name,
		contractId: data.bubo_contract_id,
		employeeId: data.bubo_employee_id,
		active:
			data.employment_status &&
			data.employment_status.toLowerCase() === "active",
		dolStatus: data.dol_status,
		scheduledHours: {
			thisWeek: data.week_hours_worked.this_week,
			nextWeek: data.week_hours_worked.next_week,
			weekAfterNext: data.week_hours_worked.week_after_next,
		},
		username: data.username,
    canAutoSchedule: data.officer_risk_score <= 25
	};
};

const parseShift = (data) => {
	const shift = {
		contractLocationId: data.bubo_contract_location_id,
		contractLocationName: data.bubo_contract_location_name,
		distance: data.distance,
		employeeId: data.bubo_employee_id,
		endDate: utcToZonedTime(
			`${data.end_timestamp.split("+")[0]}Z`,
			data.timezone,
		),
		isAutoassignable: data.is_autoassignable,
		jobTitle: data.job_title,
		payrate: 10,
		scheduleType: data.schedule_type,
		shiftId: data.bubo_shift_id,
		startDate: utcToZonedTime(
			`${data.start_timestamp.split("+")[0]}Z`,
			data.timezone,
		),
		status: data.status,
		timezone: data.timezone,
		clientJobTitle: data.client_job_title,
		estimatedTotalPay: data.estimated_total_pay,

	};

	shift.dayOfMonth = format(shift.startDate, "d");
	shift.dayOfWeek = format(shift.startDate, "EEE");
	shift.dayOfYear = format(shift.startDate, "DDD");
	shift.endTime = format(shift.endDate, "HH:mm");
	shift.month = format(shift.startDate, "MMM");
	shift.startTime = format(shift.startDate, "HH:mm");
	shift.weekOfYear = Number.parseInt(format(shift.startDate, "w"));
	shift.year = Number.parseInt(format(shift.startDate, "yyyy"));
	shift.yearWeek = format(startOfWeek(shift.startDate), "yyyyMMdd");
	shift.displayDate = `${shift.dayOfWeek} ${shift.month}/${shift.dayOfMonth}`;
	shift.date = format(shift.startDate, "yyyy-MM-dd");

	return shift;
};

const parseCase = (data) => {
	const [year, month, day] = data.start_date.split("-");
	const c = {
		contactId: data.contact_id,
		id: data.id,
		status: data.status,
		startDate: new Date(year, month - 1, day),
		shiftType: data.shift_type,
	};

	c.dayOfMonth = format(c.startDate, "d");
	c.dayOfWeek = format(c.startDate, "EEE");
	c.dayOfYear = format(c.startDate, "DDD");
	c.month = format(c.startDate, "MMM");
	c.startTime = format(c.startDate, "HH:mm");
	c.weekOfYear = Number.parseInt(format(c.startDate, "w"));
	c.year = Number.parseInt(format(c.startDate, "yyyy"));
	c.yearWeek = format(startOfWeek(c.startDate), "yyyyMMdd");
	c.displayDate = `${c.dayOfWeek} ${c.month}/${c.dayOfMonth}`;

	return c;
};

export const http = {
	getCases: async () => {
		const url = "/case";
		const response = await axiosShyftr.get(url);
		return response.data.map(parseCase);
	},
	getMe: async () => {
		const url = "/me";
		const response = await axiosShyftr.get(url);

		return parseMe(response.data);
	},
	getShifts: async () => {
		const url = "/shift";
		const response = await axiosShyftr.get(url);
		const date = new Date();
		const dateUtc = Date.UTC(
			date.getUTCFullYear(),
			date.getUTCMonth(),
			date.getUTCDate(),
			date.getUTCHours(),
			date.getUTCMinutes(),
			date.getUTCSeconds(),
		);
		const nowUtc = new Date(dateUtc);
		const shifts = {
			unassignedShifts: response.data.unassigned_shifts.map(parseShift),
			currentShifts: response.data.current_shifts
				.map(parseShift)
				.filter((shift) => {
					const thisWeek = Number.parseInt(format(nowUtc, "w"));
					const shiftWeek = Number.parseInt(format(shift.startDate, "w"));

					return shiftWeek >= thisWeek;
				}),
		};

		shifts.unassignedShifts.sort((thisShift, thatShift) =>
			compareAsc(thisShift.startDate, thatShift.startDate),
		);
		shifts.currentShifts.sort((thisShift, thatShift) =>
			compareAsc(thisShift.startDate, thatShift.startDate),
		);

		return shifts;
	},
	getAuth: async (phoneNumber) => {
		const url = `/otp?phone_number=%2B1${phoneNumber}`;
		const response = await axiosAuth.get(url);

		return response.data;
	},
	getToken: async ({ otp, phoneNumber, session }) => {
		const url = `/token`;
		const data = { otp, phone_number: `+1${phoneNumber}`, session };
		const response = await axiosAuth.post(url, data);

		return response.data.IdToken;
	},
	requestShift: async (shiftIds) => {
		const url = `/shift`;
		const response = await axiosShyftr.post(url, { shift_ids: shiftIds });

		return response.data;
	},
	requestAvailability: async ({ employeeId, availabilityDate, shiftType }) => {
		const response = await axiosShyftr.post("/case", {
			bubo_employee_id: employeeId,
			availability_date: availabilityDate,
			shift_type: shiftType,
		});

		return response.data;
	},
	enableAvailability: async (caseId) => {
		const response = await axiosShyftr.put(`/case/${caseId}?cancel=false`);

		return response.data;
	},
	cancelAvailability: async (caseId) => {
		const response = await axiosShyftr.put(`/case/${caseId}?cancel=true`);

		return response.data;
	},
};
